/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import apiService from "services/apiService";
import { FormProvider, useForm } from "react-hook-form";
import Select from "react-select";
import Input from "components/Input";
import Button from "components/Button";
import { useToaster } from "contexts/toasterContext";
import ConfirmChangesModal from "./ConfirmChangesModal";
import GenericModal from "components/Modal";

export interface SettingValue<T> {
    globalValue: T;
    userValue: T | null;
    effectiveValue: T;
    isOverridden: boolean;
}

export interface UserTrackingSettings {
    screenshotEnabled: SettingValue<boolean>;
    deviceCheckInterval: SettingValue<number>;
    dailyBreakLimit: SettingValue<number>;
    sessionUploadInterval: SettingValue<number>;
    urlSessionUploadInterval: SettingValue<number>;
    deviceStatusUploadInterval: SettingValue<number>;
    presenceCheckInterval: SettingValue<number>;
    presenceUploadInterval: SettingValue<number>;
    scheduleTrackingEnabled: SettingValue<boolean>;
    idleThreshold: SettingValue<number>;
    screenshotInterval: SettingValue<number>;
    workStartTime: SettingValue<string>;
    workEndTime: SettingValue<string>;
    workDays: SettingValue<number[]>;
    dashboardEnabled?: SettingValue<boolean>;
    screenshotQuality?: SettingValue<string>;
    liveScreenQuality?: SettingValue<string>;
    appTrackingEnabled?: SettingValue<boolean>;
    urlTrackingEnabled?: SettingValue<boolean>;
    presenceTrackingEnabled?: SettingValue<boolean>;
    deviceTrackingEnabled?: SettingValue<boolean>;
    wifiTrackingEnabled?: SettingValue<boolean>;
    liveScreenEnabled?: SettingValue<boolean>;
}

// Updated to full day names
const DAYS_OF_WEEK = [
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
    { value: 0, label: "Sunday" },
];

const QUALITY_OPTIONS = [
    { value: "low", label: "Low", resolution: "960 × 540", size: "5 fps", color: "text-green-600" },
    { value: "medium", label: "Medium", resolution: "1280 × 720", size: "10 fps", color: "text-orange-500" },
    { value: "high", label: "High", resolution: "1920 × 1080", size: "20 fps", color: "text-blue-600" },
];

const formatQualityOption = (data: any, { context }: any) => {
    if (context === "value") {
        return (
            <div className="flex items-center text-sm font-medium">
                <span className={data.color || "text-gray-900 dark:text-white"}>
                    {data.label}
                </span>
            </div>
        );
    }
    return (
        <div className="flex items-center justify-between text-sm">
            <span className={`font-semibold ${data.color || "text-gray-900 dark:text-white"}`}>
                {data.label}
            </span>
            <div className="text-gray-500 dark:text-gray-400 flex items-center text-xs">
                {data.resolution}
            </div>
        </div>
    );
};

const getDayName = (dayNum: number): string => DAYS_OF_WEEK.find((d) => d.value === dayNum)?.label || "";

/* ---------------------------------------------------------------------- *
 * Reusable Table Layout Components
 * ---------------------------------------------------------------------- */
const TableContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mt-3 mb-8 bg-white dark:bg-gray-800 shadow-sm">
        {children}
    </div>
);

const TableHeader = () => (
    <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="w-[40%] md:w-[32%] py-3 px-4 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Setting</div>
        <div className="w-[25%] md:w-[22%] py-3 px-4 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Global Setting</div>
        <div className="flex-1 py-3 px-4 text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/50 dark:bg-blue-900/10 border-l border-gray-200 dark:border-gray-700">User Setting</div>
    </div>
);

const TableRow: React.FC<{
    label: string;
    isOverridden: boolean;
    globalElement: React.ReactNode;
    children: React.ReactNode;
}> = ({ label, isOverridden, globalElement, children }) => (
    <div className="flex items-stretch border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 hover:bg-gray-50/30 dark:hover:bg-gray-800/50 transition-colors">
        <div className="w-[40%] md:w-[32%] py-3 px-4 text-[13px] font-medium text-gray-700 dark:text-gray-300 flex items-center">
            {label}
            {isOverridden && (
                <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 shrink-0">
                    custom
                </span>
            )}
        </div>
        <div className="w-[25%] md:w-[22%] py-3 px-4 text-[13px] text-gray-600 dark:text-gray-400 flex items-center">
            {globalElement}
        </div>
        <div className="flex-1 py-3 px-4 bg-blue-50/50 dark:bg-blue-900/10 border-l border-gray-100 dark:border-gray-700/50 flex items-center min-w-0">
            {children}
        </div>
    </div>
);

const GlobalCheckbox: React.FC<{ checked: boolean }> = ({ checked }) => (
    <input
        type="checkbox"
        readOnly
        checked={checked}
        className="ml-1 w-4 h-4 rounded border-gray-300 bg-gray-100 text-gray-400 focus:ring-0 cursor-default dark:border-gray-600 dark:bg-gray-700 opacity-60"
    />
);

const UserCheckbox: React.FC<{ checked: boolean; onChange: (e: any) => void }> = ({ checked, onChange }) => (
    <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="ml-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 dark:border-gray-600 dark:bg-gray-700 cursor-pointer"
    />
);

const GlobalWorkDays: React.FC<{ days: number[] | undefined | null }> = ({ days }) => {
    if (!days || days.length === 0) return <span className="text-gray-400">—</span>;
    return (
        <div className="flex gap-1 flex-wrap">
            {[...days].sort((a, b) => a - b).map(d => (
                <span key={d} className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[11px] font-medium dark:bg-blue-900/40 dark:text-blue-300 shrink-0">
                    {getDayName(d)}
                </span>
            ))}
        </div>
    );
};

const InputWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="w-full max-w-[160px] [&>div]:!m-0 [&>div>label]:!hidden [&>div>div:last-child]:!hidden flex items-center [color-scheme:light] dark:[color-scheme:dark]">
        {children}
    </div>
);

const defaultSelectClassNames = {
    control: (state: any) =>
        `border rounded px-2 text-sm ${state.isFocused ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-300 dark:border-gray-600"
        } bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-150 min-h-[36px]`,
    menu: () => "mt-1 rounded shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-50",
    option: (state: any) =>
        `px-3 py-2 cursor-pointer text-sm border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${state.isFocused ? "bg-gray-50 dark:bg-gray-700/50" : "bg-white dark:bg-gray-800"
        } text-gray-900 dark:text-white`,
};

/* ---------------------------------------------------------------------- *
 * Reset Confirmation Popup Component
 * ---------------------------------------------------------------------- */
const ResetPopup: React.FC<{
    handleConfirm: () => void;
    onClose: () => void;
    isSubmitting: boolean;
}> = ({ handleConfirm, onClose, isSubmitting }) => (
    <div className="p-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Are you sure you want to clear all user overrides for this employee? All tracking behavior will revert to the current Global Settings. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
                type="button"
                color="gray"
                onClick={onClose}
                disabled={isSubmitting}
                className="bg-gray-500 hover:bg-gray-600 text-white"
            >
                Cancel
            </Button>
            <Button
                type="button"
                color="blue"
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
            >
                {isSubmitting ? "Resetting..." : "Confirm"}
            </Button>
        </div>
    </div>
);

/* ---------------------------------------------------------------------- *
 * Main User Settings Component
 * ---------------------------------------------------------------------- */
const UserSettings: React.FC<{ employeeId: string | number }> = ({ employeeId }) => {
    const [settings, setSettings] = useState<UserTrackingSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { addToast } = useToaster();
    const methods = useForm<any>();

    const [initialFormValues, setInitialFormValues] = useState<any>({});
    const [diffPayload, setDiffPayload] = useState<any>({});
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Boolean & Quality states
    const [screenshotEnabled, setScreenshotEnabled] = useState(false);
    const [screenshotQuality, setScreenshotQuality] = useState(QUALITY_OPTIONS[1]);
    const [liveScreenQuality, setLiveScreenQuality] = useState(QUALITY_OPTIONS[1]);
    const [dashboardEnabled, setDashboardEnabled] = useState(false);
    const [appTrackingEnabled, setAppTrackingEnabled] = useState(false);
    const [urlTrackingEnabled, setUrlTrackingEnabled] = useState(true);
    const [presenceTrackingEnabled, setPresenceTrackingEnabled] = useState(false);
    const [deviceTrackingEnabled, setDeviceTrackingEnabled] = useState(true);
    const [wifiTrackingEnabled, setWifiTrackingEnabled] = useState(true);
    const [liveScreenEnabled, setLiveScreenEnabled] = useState(true);
    const [scheduleTrackingEnabled, setScheduleTrackingEnabled] = useState(false);
    const [selectedDays, setSelectedDays] = useState<any[]>([]);

    const fetchUserSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiService.get(`/api/tracking-user-settings/get/${employeeId}`);
            if (response.data) {
                setSettings(response.data);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to load user settings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employeeId) fetchUserSettings();
    }, [employeeId]);

    useEffect(() => {
        if (settings) {
            const effQuality = (settings.screenshotQuality?.effectiveValue ?? "medium").toLowerCase();
            const effLiveQuality = (settings.liveScreenQuality?.effectiveValue ?? "medium").toLowerCase();

            const initValues = {
                screenshotInterval: settings.screenshotInterval?.effectiveValue ?? 600,
                deviceCheckInterval: settings.deviceCheckInterval?.effectiveValue ?? 10800,
                dailyBreakLimit: settings.dailyBreakLimit?.effectiveValue ?? 5400,
                sessionUploadInterval: settings.sessionUploadInterval?.effectiveValue ?? 3600,
                urlSessionUploadInterval: settings.urlSessionUploadInterval?.effectiveValue ?? 3600,
                deviceStatusUploadInterval: settings.deviceStatusUploadInterval?.effectiveValue ?? 600,
                presenceCheckInterval: settings.presenceCheckInterval?.effectiveValue ?? 600,
                presenceUploadInterval: settings.presenceUploadInterval?.effectiveValue ?? 3600,
                idleThreshold: settings.idleThreshold?.effectiveValue ?? 130,
                workStartTime: settings.workStartTime?.effectiveValue ?? "10:00",
                workEndTime: settings.workEndTime?.effectiveValue ?? "18:00",
            };

            const completeInitState = {
                ...initValues,
                screenshotEnabled: settings.screenshotEnabled?.effectiveValue ?? false,
                screenshotQuality: effQuality.toUpperCase(),
                liveScreenQuality: effLiveQuality.toUpperCase(),
                dashboardEnabled: settings.dashboardEnabled?.effectiveValue ?? false,
                appTrackingEnabled: settings.appTrackingEnabled?.effectiveValue ?? false,
                urlTrackingEnabled: settings.urlTrackingEnabled?.effectiveValue ?? true,
                presenceTrackingEnabled: settings.presenceTrackingEnabled?.effectiveValue ?? false,
                deviceTrackingEnabled: settings.deviceTrackingEnabled?.effectiveValue ?? true,
                wifiTrackingEnabled: settings.wifiTrackingEnabled?.effectiveValue ?? true,
                liveScreenEnabled: settings.liveScreenEnabled?.effectiveValue ?? true,
                scheduleTrackingEnabled: settings.scheduleTrackingEnabled?.effectiveValue ?? false,
                workDays: (settings.workDays?.effectiveValue ?? []).sort(),
            };

            setInitialFormValues(completeInitState);
            methods.reset(initValues);

            setScreenshotEnabled(completeInitState.screenshotEnabled);
            setScreenshotQuality(QUALITY_OPTIONS.find((o) => o.value === effQuality) || QUALITY_OPTIONS[1]);
            setLiveScreenQuality(QUALITY_OPTIONS.find((o) => o.value === effLiveQuality) || QUALITY_OPTIONS[1]);
            setDashboardEnabled(completeInitState.dashboardEnabled);
            setAppTrackingEnabled(completeInitState.appTrackingEnabled);
            setUrlTrackingEnabled(completeInitState.urlTrackingEnabled);
            setPresenceTrackingEnabled(completeInitState.presenceTrackingEnabled);
            setDeviceTrackingEnabled(completeInitState.deviceTrackingEnabled);
            setWifiTrackingEnabled(completeInitState.wifiTrackingEnabled);
            setLiveScreenEnabled(completeInitState.liveScreenEnabled);
            setScheduleTrackingEnabled(completeInitState.scheduleTrackingEnabled);

            setSelectedDays(DAYS_OF_WEEK.filter((day) => completeInitState.workDays.includes(day.value)));
        }
    }, [settings]);

    const currentDaysStr = selectedDays.map((d) => d.value).sort().join(",");
    const initialDaysStr = (initialFormValues.workDays || []).join(",");

    const isStateModified =
        screenshotEnabled !== initialFormValues.screenshotEnabled ||
        screenshotQuality.value.toUpperCase() !== initialFormValues.screenshotQuality ||
        liveScreenQuality.value.toUpperCase() !== initialFormValues.liveScreenQuality ||
        scheduleTrackingEnabled !== initialFormValues.scheduleTrackingEnabled ||
        dashboardEnabled !== initialFormValues.dashboardEnabled ||
        appTrackingEnabled !== initialFormValues.appTrackingEnabled ||
        urlTrackingEnabled !== initialFormValues.urlTrackingEnabled ||
        presenceTrackingEnabled !== initialFormValues.presenceTrackingEnabled ||
        deviceTrackingEnabled !== initialFormValues.deviceTrackingEnabled ||
        wifiTrackingEnabled !== initialFormValues.wifiTrackingEnabled ||
        liveScreenEnabled !== initialFormValues.liveScreenEnabled ||
        currentDaysStr !== initialDaysStr;

    const isModified = methods.formState.isDirty || isStateModified;

    const onSubmit = (formData: any) => {
        const currentFormState: any = {
            ...formData,
            screenshotEnabled,
            screenshotQuality: screenshotQuality.value.toUpperCase(),
            liveScreenQuality: liveScreenQuality.value.toUpperCase(),
            dashboardEnabled,
            appTrackingEnabled,
            urlTrackingEnabled,
            presenceTrackingEnabled,
            deviceTrackingEnabled,
            wifiTrackingEnabled,
            liveScreenEnabled,
            scheduleTrackingEnabled,
            workDays: selectedDays.map((d) => d.value).sort(),
        };

        const diff: any = {};
        Object.keys(currentFormState).forEach((key) => {
            if (Array.isArray(currentFormState[key])) {
                if (currentFormState[key].join() !== initialFormValues[key]?.join()) {
                    diff[key] = currentFormState[key];
                }
            } else if (currentFormState[key] !== initialFormValues[key]) {
                diff[key] = currentFormState[key];
            }
        });

        if (Object.keys(diff).length === 0) {
            addToast("No changes detected to save.", "info");
            return;
        }

        setDiffPayload(diff);
        setIsConfirmModalOpen(true);
    };

    const executeApiUpdate = async () => {
        try {
            setIsSubmitting(true);
            const response = await apiService.post(`/api/tracking-user-settings/update/${employeeId}`, diffPayload);
            if (response.status === 200 || response.status === 201) {
                addToast("User settings updated successfully!", "success");
                setIsConfirmModalOpen(false);
                fetchUserSettings();
                window.dispatchEvent(new Event("featuresEnabledChanged"));
            }
        } catch (e: any) {
            addToast(e?.response?.data?.message || "Failed to update user settings", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmReset = async () => {
        try {
            setIsSubmitting(true);
            const response = await apiService.delete(`/api/tracking-user-settings/reset/${employeeId}`);
            if (response.status === 200 || response.status === 204) {
                addToast("User settings successfully reset to Global Defaults.", "success");
                setIsResetModalOpen(false);
                fetchUserSettings();
                window.dispatchEvent(new Event("featuresEnabledChanged"));
            }
        } catch (e: any) {
            addToast(e?.response?.data?.message || "Failed to reset user settings.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-gray-300 dark:border-gray-600 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin mb-4" />
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading user settings...</p>
            </div>
        );
    }

    if (error || !settings) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <svg className="w-10 h-10 text-red-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error || "No settings found for this user."}</p>
                <Button onClick={fetchUserSettings} type="button" color="blue">Retry</Button>
            </div>
        );
    }

    const todayDateStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    return (
        <div className="py-2 px-2 max-w-6xl mx-auto">
            {/* Header Area */}
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h1 className="text-[22px] font-bold text-gray-900 dark:text-white leading-tight">User settings</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Configure tracking, schedule, and feature behavior for this user
                    </p>
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 font-medium text-right whitespace-nowrap pt-1">
                    User ID {employeeId} &bull; Updated {todayDateStr}
                </div>
            </div>

            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>

                    {/* Section: Tracking & Schedule */}
                    <h2 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Tracking & Schedule</h2>
                    <TableContainer>
                        <TableHeader />

                        <TableRow
                            label="Screenshot enabled"
                            isOverridden={!!settings.screenshotEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.screenshotEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={screenshotEnabled} onChange={(e) => setScreenshotEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="Screenshot interval (sec)"
                            isOverridden={!!settings.screenshotInterval?.isOverridden}
                            globalElement={settings.screenshotInterval?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="screenshotInterval" label="Screenshot Interval" type="number" min={60} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Live screen enabled"
                            isOverridden={!!settings.liveScreenEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.liveScreenEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={liveScreenEnabled} onChange={(e) => setLiveScreenEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="Screenshot quality"
                            isOverridden={!!settings.screenshotQuality?.isOverridden}
                            globalElement={settings.screenshotQuality?.globalValue ? settings.screenshotQuality.globalValue.charAt(0).toUpperCase() + settings.screenshotQuality.globalValue.slice(1).toLowerCase() : "—"}
                        >
                            <div className="w-full max-w-[160px] relative">
                                <Select
                                    value={screenshotQuality}
                                    onChange={(selected) => setScreenshotQuality(selected as any)}
                                    options={QUALITY_OPTIONS}
                                    formatOptionLabel={formatQualityOption}
                                    classNamePrefix="select"
                                    unstyled
                                    classNames={defaultSelectClassNames}
                                    placeholder="—"
                                    menuPosition="fixed"
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            </div>
                        </TableRow>

                        <TableRow
                            label="Live screen quality"
                            isOverridden={!!settings.liveScreenQuality?.isOverridden}
                            globalElement={settings.liveScreenQuality?.globalValue ? settings.liveScreenQuality.globalValue.charAt(0).toUpperCase() + settings.liveScreenQuality.globalValue.slice(1).toLowerCase() : "—"}
                        >
                            <div className="w-full max-w-[160px] relative">
                                <Select
                                    value={liveScreenQuality}
                                    onChange={(selected) => setLiveScreenQuality(selected as any)}
                                    options={QUALITY_OPTIONS}
                                    formatOptionLabel={formatQualityOption}
                                    classNamePrefix="select"
                                    unstyled
                                    classNames={defaultSelectClassNames}
                                    placeholder="—"
                                    menuPosition="fixed"
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            </div>
                        </TableRow>

                        <TableRow
                            label="Idle threshold (sec)"
                            isOverridden={!!settings.idleThreshold?.isOverridden}
                            globalElement={settings.idleThreshold?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="idleThreshold" label="Idle Threshold" type="number" min={30} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Device check interval (sec)"
                            isOverridden={!!settings.deviceCheckInterval?.isOverridden}
                            globalElement={settings.deviceCheckInterval?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="deviceCheckInterval" label="Device Check Interval" type="number" min={60} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Daily break limit (sec)"
                            isOverridden={!!settings.dailyBreakLimit?.isOverridden}
                            globalElement={settings.dailyBreakLimit?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="dailyBreakLimit" label="Daily Break Limit" type="number" min={0} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Session upload interval (sec)"
                            isOverridden={!!settings.sessionUploadInterval?.isOverridden}
                            globalElement={settings.sessionUploadInterval?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="sessionUploadInterval" label="Session Upload" type="number" min={60} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="URL session upload interval (sec)"
                            isOverridden={!!settings.urlSessionUploadInterval?.isOverridden}
                            globalElement={settings.urlSessionUploadInterval?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="urlSessionUploadInterval" label="URL Upload" type="number" min={60} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Device status upload interval (sec)"
                            isOverridden={!!settings.deviceStatusUploadInterval?.isOverridden}
                            globalElement={settings.deviceStatusUploadInterval?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="deviceStatusUploadInterval" label="Status Upload" type="number" min={60} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Presence check interval (sec)"
                            isOverridden={!!settings.presenceCheckInterval?.isOverridden}
                            globalElement={settings.presenceCheckInterval?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="presenceCheckInterval" label="Presence Check" type="number" min={60} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Presence upload interval (sec)"
                            isOverridden={!!settings.presenceUploadInterval?.isOverridden}
                            globalElement={settings.presenceUploadInterval?.globalValue ?? "—"}
                        >
                            <InputWrapper>
                                <Input name="presenceUploadInterval" label="Presence Upload" type="number" min={60} placeholder="—" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Schedule tracking enabled"
                            isOverridden={!!settings.scheduleTrackingEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.scheduleTrackingEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={scheduleTrackingEnabled} onChange={(e) => setScheduleTrackingEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="Work start time"
                            isOverridden={!!settings.workStartTime?.isOverridden}
                            globalElement={settings.workStartTime?.globalValue || "—"}
                        >
                            <InputWrapper>
                                <Input name="workStartTime" label="Start Time" type="time" placeholder="--:--" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Work end time"
                            isOverridden={!!settings.workEndTime?.isOverridden}
                            globalElement={settings.workEndTime?.globalValue || "—"}
                        >
                            <InputWrapper>
                                <Input name="workEndTime" label="End Time" type="time" placeholder="--:--" />
                            </InputWrapper>
                        </TableRow>

                        <TableRow
                            label="Work days"
                            isOverridden={!!settings.workDays?.isOverridden}
                            globalElement={<GlobalWorkDays days={settings.workDays?.globalValue} />}
                        >
                            <div className="w-full relative flex items-center">
                                <Select
                                    isMulti
                                    value={selectedDays}
                                    onChange={(selected) => setSelectedDays(selected as any)}
                                    options={DAYS_OF_WEEK}
                                    classNamePrefix="select"
                                    placeholder="Select work days..."
                                    unstyled
                                    className="w-full"
                                    menuPosition="fixed"
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    classNames={{
                                        control: (state) =>
                                            `min-h-[42px] border rounded-lg px-2 w-full flex overflow-hidden ${state.isFocused
                                                ? "border-blue-500 ring-1 ring-blue-500"
                                                : "border-gray-300 dark:border-gray-600"
                                            } bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200`,
                                        valueContainer: () =>
                                            "flex flex-nowrap overflow-x-auto gap-1 py-1 items-center [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] w-full",
                                        menu: () =>
                                            "mt-1 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-[9999]",
                                        menuList: () => "py-1",
                                        option: (state) =>
                                            `px-3 py-2 cursor-pointer ${state.isFocused
                                                ? "bg-gray-100 dark:bg-gray-700"
                                                : "bg-white dark:bg-gray-800"
                                            } text-gray-900 dark:text-gray-200`,
                                        multiValue: () => "bg-blue-500 rounded flex shrink-0 items-center",
                                        multiValueLabel: () => "text-white px-2 py-0.5 text-sm whitespace-nowrap",
                                        multiValueRemove: () =>
                                            "text-white hover:bg-blue-600 hover:text-white px-1 rounded-r cursor-pointer transition-colors flex items-center justify-center",
                                        input: () => "text-gray-900 dark:text-white m-0.5",
                                        placeholder: () =>
                                            "text-gray-500 dark:text-gray-400 mx-0.5 whitespace-nowrap",
                                        clearIndicator: () =>
                                            "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer p-1 shrink-0",
                                        dropdownIndicator: () =>
                                            "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer p-1 shrink-0",
                                    }}
                                />
                            </div>
                        </TableRow>
                    </TableContainer>

                    {/* Section: Feature Controls */}
                    <h2 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 mt-10">Feature Controls</h2>
                    <TableContainer>
                        <TableHeader />

                        <TableRow
                            label="Dashboard enabled"
                            isOverridden={!!settings.dashboardEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.dashboardEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={dashboardEnabled} onChange={(e) => setDashboardEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="App tracking enabled"
                            isOverridden={!!settings.appTrackingEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.appTrackingEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={appTrackingEnabled} onChange={(e) => setAppTrackingEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="URL tracking enabled"
                            isOverridden={!!settings.urlTrackingEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.urlTrackingEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={urlTrackingEnabled} onChange={(e) => setUrlTrackingEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="Presence tracking enabled"
                            isOverridden={!!settings.presenceTrackingEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.presenceTrackingEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={presenceTrackingEnabled} onChange={(e) => setPresenceTrackingEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="Device tracking enabled"
                            isOverridden={!!settings.deviceTrackingEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.deviceTrackingEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={deviceTrackingEnabled} onChange={(e) => setDeviceTrackingEnabled(e.target.checked)} />
                        </TableRow>

                        <TableRow
                            label="Wifi tracking enabled"
                            isOverridden={!!settings.wifiTrackingEnabled?.isOverridden}
                            globalElement={<GlobalCheckbox checked={!!settings.wifiTrackingEnabled?.globalValue} />}
                        >
                            <UserCheckbox checked={wifiTrackingEnabled} onChange={(e) => setWifiTrackingEnabled(e.target.checked)} />
                        </TableRow>
                    </TableContainer>

                    {/* Bottom Actions */}
                    <div className="flex justify-between items-center mt-8 pb-4">
                        <Button
                            type="button"
                            color="gray"
                            onClick={() => setIsResetModalOpen(true)}
                            disabled={isSubmitting}
                            className="bg-gray-500 hover:bg-gray-600 text-white shadow-sm border border-transparent"
                        >
                            Reset to global
                        </Button>
                        <Button
                            type="submit"
                            color="blue"
                            disabled={!isModified || isSubmitting}
                            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm border border-transparent disabled:opacity-50"
                        >
                            Update settings
                        </Button>
                    </div>

                </form>
            </FormProvider>

            {/* Modals */}
            <ConfirmChangesModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={executeApiUpdate}
                changes={diffPayload}
                initialValues={initialFormValues}
                isSubmitting={isSubmitting}
            />

            {isResetModalOpen && (
                <GenericModal
                    isOpen={isResetModalOpen}
                    onClose={() => !isSubmitting && setIsResetModalOpen(false)}
                    header="Reset User Settings"
                >
                    <ResetPopup
                        handleConfirm={handleConfirmReset}
                        onClose={() => setIsResetModalOpen(false)}
                        isSubmitting={isSubmitting}
                    />
                </GenericModal>
            )}
        </div>
    );
};

export default UserSettings;